import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Chip,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
  Grid,
  Card,
  CardContent,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  Tab,
  Tabs
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Search as SearchIcon,
  Settings as AttributeIcon,
  AutoAwesome as AIIcon,
  CheckCircle as CheckIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Category as CategoryIcon,
  Accessibility as AccessibilityIcon,
  Restaurant as FoodIcon,
  LocalOffer as ServiceIcon,
  Payment as PaymentIcon,
  Pets as PetIcon,
  MusicNote as EntertainmentIcon,
  HealthAndSafety as HealthIcon,
  Computer as TechIcon,
  Business as BusinessIcon,
  Language as LanguageIcon,
  Star as SpecialIcon,
  Work as ProfessionalIcon
} from '@mui/icons-material';

const AttributeManager = ({ 
  open, 
  onClose, 
  currentAttributes = [], 
  onSave,
  businessData = {}
}) => {
  const [selectedAttributes, setSelectedAttributes] = useState(currentAttributes);
  const [allAttributes, setAllAttributes] = useState([]);
  const [categorizedAttributes, setCategorizedAttributes] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Category icons mapping
  const categoryIcons = {
    accessibility: <AccessibilityIcon />,
    amenities: <AttributeIcon />,
    family: <CheckIcon />,
    food: <FoodIcon />,
    services: <ServiceIcon />,
    payments: <PaymentIcon />,
    pets: <PetIcon />,
    entertainment: <EntertainmentIcon />,
    health: <HealthIcon />,
    technology: <TechIcon />,
    business: <BusinessIcon />,
    languages: <LanguageIcon />,
    special: <SpecialIcon />,
    professional: <ProfessionalIcon />
  };

  // Category display names
  const categoryNames = {
    accessibility: 'Barrierefreiheit',
    amenities: 'Ausstattung',
    family: 'Familie & Kinder',
    food: 'Essen & Trinken',
    services: 'Dienstleistungen',
    payments: 'Zahlungsmethoden',
    pets: 'Haustiere',
    entertainment: 'Unterhaltung',
    health: 'Gesundheit & Sicherheit',
    technology: 'Technologie',
    business: 'Branchenspezifisch',
    languages: 'Sprachen',
    special: 'Besondere Merkmale',
    professional: 'Professionelle Services'
  };

  // Fetch all attributes on mount
  useEffect(() => {
    if (open) {
      fetchAttributes();
      if (businessData?.business_name) {
        fetchAISuggestions();
      }
    }
  }, [open, businessData]);

  const fetchAttributes = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/attributes', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      const data = await response.json();
      if (data.success) {
        setAllAttributes(data.attributes);
        setCategorizedAttributes(data.categorized);
      }
    } catch (error) {
      console.error('Error fetching attributes:', error);
    }
    setLoading(false);
  };

  const fetchAISuggestions = async () => {
    setLoadingSuggestions(true);
    try {
      const response = await fetch('/api/attributes/suggest', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          businessName: businessData.business_name,
          businessDescription: businessData.business_description || '',
          categories: businessData.categories?.map(cat => cat.label || cat.displayName) || [],
          currentAttributes: selectedAttributes.map(attr => attr.key),
          location: businessData.business_address || ''
        })
      });
      const data = await response.json();
      if (data.success) {
        setAiSuggestions(data.suggestions);
      }
    } catch (error) {
      console.error('Error fetching AI suggestions:', error);
    }
    setLoadingSuggestions(false);
  };

  const addAttribute = (attribute) => {
    const exists = selectedAttributes.some(attr => attr.key === attribute.key);
    if (!exists) {
      setSelectedAttributes([...selectedAttributes, attribute]);
    }
  };

  const removeAttribute = (attributeKey) => {
    setSelectedAttributes(selectedAttributes.filter(attr => attr.key !== attributeKey));
  };

  const addAISuggestion = (suggestion) => {
    addAttribute(suggestion);
  };

  const handleSave = () => {
    onSave(selectedAttributes);
    onClose();
  };

  // Filter attributes based on search term
  const filteredAttributes = useMemo(() => {
    if (!searchTerm) return [];
    return allAttributes.filter(attr => 
      attr.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      attr.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      attr.key.toLowerCase().includes(searchTerm.toLowerCase())
    ).filter(attr => 
      !selectedAttributes.some(selected => selected.key === attr.key)
    ).slice(0, 20);
  }, [searchTerm, allAttributes, selectedAttributes]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const renderAttributesByCategory = () => {
    const categories = Object.keys(categorizedAttributes);
    
    return categories.map((categoryKey) => {
      const categoryAttrs = categorizedAttributes[categoryKey] || [];
      const availableAttrs = categoryAttrs.filter(attr => 
        !selectedAttributes.some(selected => selected.key === attr.key)
      );
      const selectedInCategory = categoryAttrs.filter(attr => 
        selectedAttributes.some(selected => selected.key === attr.key)
      );
      
      if (categoryAttrs.length === 0) return null;

      return (
        <Accordion key={categoryKey} sx={{ mb: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ color: '#2196f3' }}>{categoryIcons[categoryKey] || <AttributeIcon />}</Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {categoryNames[categoryKey] || categoryKey}
              </Typography>
              
              {/* Show available vs selected count */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                {selectedInCategory.length > 0 && (
                  <Chip 
                    label={`${selectedInCategory.length} ausgewählt`}
                    size="small"
                    sx={{ 
                      bgcolor: '#e8f5e8',
                      color: '#2e7d32',
                      fontWeight: 600,
                      fontSize: '0.7rem'
                    }}
                  />
                )}
                {availableAttrs.length > 0 && (
                  <Badge badgeContent={availableAttrs.length} color="primary" sx={{ '& .MuiBadge-badge': { fontSize: '0.7rem' } }} />
                )}
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {/* Show selected attributes in this category first */}
            {selectedInCategory.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, color: '#2e7d32', fontWeight: 600 }}>
                  Bereits ausgewählt in dieser Kategorie:
                </Typography>
                <Grid container spacing={1}>
                  {selectedInCategory.map((attr) => (
                    <Grid item xs={12} sm={6} md={4} key={attr.key}>
                      <Card 
                        sx={{ 
                          p: 2, 
                          border: '2px solid #4caf50',
                          bgcolor: '#f1f8e9',
                          cursor: 'pointer',
                          '&:hover': { 
                            bgcolor: '#e8f5e8'
                          }
                        }}
                        onClick={() => removeAttribute(attr.key)}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: '#2e7d32' }}>
                              {attr.label}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#4caf50', fontSize: '0.75rem' }}>
                              {attr.description}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            sx={{ color: '#4caf50' }}
                          >
                            <RemoveIcon />
                          </IconButton>
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
                <Divider sx={{ my: 3 }} />
              </Box>
            )}
            
            {/* Available attributes */}
            {availableAttrs.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 2, color: '#666', fontWeight: 600 }}>
                  Verfügbare Attribute:
                </Typography>
                <Grid container spacing={1}>
                  {availableAttrs.map((attr) => {
                const isSelected = selectedAttributes.some(selected => selected.key === attr.key);
                
                return (
                  <Grid item xs={12} sm={6} md={4} key={attr.key}>
                    <Card 
                      sx={{ 
                        p: 2, 
                        cursor: 'pointer',
                        border: isSelected ? '2px solid #4caf50' : '1px solid #e0e0e0',
                        bgcolor: isSelected ? '#f1f8e9' : 'white',
                        position: 'relative',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': { 
                          bgcolor: isSelected ? '#e8f5e8' : '#f5f5f5',
                          borderColor: isSelected ? '#4caf50' : '#2196f3',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }
                      }}
                      onClick={() => isSelected ? removeAttribute(attr.key) : addAttribute(attr)}
                    >
                      {/* Selected indicator */}
                      {isSelected && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            width: 24,
                            height: 24,
                            bgcolor: '#4caf50',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '0.75rem'
                          }}
                        >
                          ✓
                        </Box>
                      )}
                      
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 600, 
                          mb: 1,
                          color: isSelected ? '#2e7d32' : '#1a1a1a',
                          pr: isSelected ? 3 : 0
                        }}
                      >
                        {attr.label}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: isSelected ? '#4caf50' : '#666', 
                          fontSize: '0.75rem',
                          lineHeight: 1.3,
                          display: 'block'
                        }}
                      >
                        {attr.description}
                      </Typography>
                      
                      {/* Action indicator */}
                      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: isSelected ? '#2e7d32' : '#2196f3',
                            fontWeight: 600,
                            fontSize: '0.7rem'
                          }}
                        >
                          {isSelected ? 'AUSGEWÄHLT' : 'HINZUFÜGEN'}
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                );
              })}
                </Grid>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      );
    });
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="xl" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        bgcolor: '#fafafa', 
        borderBottom: '1px solid #e5e5e5',
        py: 3,
        px: 4
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ color: '#2196f3' }}><AttributeIcon /></Box>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
            Google Business Profile Attribute
          </Typography>
          <Chip 
            label={`${selectedAttributes.length} ausgewählt`}
            size="small"
            sx={{ 
              bgcolor: '#e3f2fd', 
              color: '#1976d2',
              fontWeight: 500,
              border: '1px solid #2196f3'
            }}
          />
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange} sx={{ px: 4 }}>
            <Tab label="KI-Empfehlungen" />
            <Tab label="Ausgewählte Attribute" />
            <Tab label="Alle Attribute" />
          </Tabs>
        </Box>

        <Box sx={{ p: 4 }}>
          {activeTab === 0 && (
            <Box>
              {/* KI-Empfehlungen */}
              <Box sx={{ p: 4, bgcolor: '#f8f9ff', borderRadius: 2, border: '2px solid #2196f3', mb: 4, overflow: 'visible' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Box sx={{ color: '#2196f3' }}><AIIcon /></Box>
                  <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 600 }}>
                    KI-Empfehlungen für Ihr Unternehmen
                  </Typography>
                  {loadingSuggestions && <CircularProgress size={20} sx={{ color: '#2196f3' }} />}
                </Box>
                  
                {aiSuggestions.length > 0 ? (
                  <Box sx={{ pt: 3, overflow: 'visible' }}>
                    <Grid container spacing={2}>
                      {aiSuggestions.map((suggestion) => {
                        const isAlreadySelected = selectedAttributes.some(attr => attr.key === suggestion.key);
                        
                        return (
                          <Grid item xs={12} sm={6} md={4} key={suggestion.key}>
                            <Box sx={{ position: 'relative', pt: 2 }}>
                              {/* KI Badge - jetzt außerhalb der Card */}
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 12,
                                  bgcolor: '#2196f3',
                                  color: 'white',
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: 2,
                                  fontSize: '0.7rem',
                                  fontWeight: 600,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  zIndex: 10,
                                  boxShadow: '0 2px 4px rgba(33, 150, 243, 0.3)'
                                }}
                              >
                                <AIIcon sx={{ fontSize: '0.8rem' }} />
                                KI-EMPFEHLUNG
                              </Box>
                              
                              <Card 
                                sx={{ 
                                  p: 3, 
                                  pt: 4, // Extra Platz oben für das Badge
                                  border: isAlreadySelected ? '2px solid #4caf50' : '2px solid #2196f3',
                                  bgcolor: isAlreadySelected ? '#f1f8e9' : 'white',
                                  cursor: 'pointer',
                                  position: 'relative',
                                  transition: 'all 0.2s ease-in-out',
                                  '&:hover': { 
                                    bgcolor: isAlreadySelected ? '#e8f5e8' : '#e3f2fd',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 6px 20px rgba(33, 150, 243, 0.3)'
                                  }
                                }}
                                onClick={() => {
                                  if (isAlreadySelected) {
                                    removeAttribute(suggestion.key);
                                  } else {
                                    addAISuggestion(suggestion);
                                  }
                                }}
                              >
                                {/* Selected indicator for AI suggestions */}
                                {isAlreadySelected && (
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      top: 8,
                                      right: 8,
                                      width: 28,
                                      height: 28,
                                      bgcolor: '#4caf50',
                                      borderRadius: '50%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      color: 'white',
                                      fontSize: '0.9rem',
                                      boxShadow: '0 2px 8px rgba(76, 175, 80, 0.4)'
                                    }}
                                  >
                                    ✓
                                  </Box>
                                )}
                                
                                <Typography 
                                  variant="h6" 
                                  sx={{ 
                                    fontWeight: 700, 
                                    mb: 1.5, 
                                    color: isAlreadySelected ? '#2e7d32' : '#1976d2',
                                    pr: isAlreadySelected ? 4 : 0
                                  }}
                                >
                                  {suggestion.label}
                                </Typography>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    color: isAlreadySelected ? '#4caf50' : '#666', 
                                    mb: 2.5,
                                    lineHeight: 1.4
                                  }}
                                >
                                  {suggestion.description}
                                </Typography>
                                
                                <Button 
                                  variant={isAlreadySelected ? "outlined" : "contained"}
                                  size="small" 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (isAlreadySelected) {
                                      removeAttribute(suggestion.key);
                                    } else {
                                      addAISuggestion(suggestion);
                                    }
                                  }}
                                  sx={{ 
                                    bgcolor: isAlreadySelected ? 'transparent' : '#2196f3',
                                    borderColor: isAlreadySelected ? '#f44336' : '#2196f3',
                                    color: isAlreadySelected ? '#f44336' : 'white',
                                    fontWeight: 600,
                                    px: 2,
                                    py: 1,
                                    '&:hover': {
                                      bgcolor: isAlreadySelected ? '#ffebee' : '#1976d2',
                                      borderColor: isAlreadySelected ? '#d32f2f' : '#1976d2'
                                    }
                                  }}
                                  startIcon={isAlreadySelected ? <RemoveIcon /> : <AddIcon />}
                                >
                                  {isAlreadySelected ? 'ENTFERNEN' : 'HINZUFÜGEN'}
                                </Button>
                              </Card>
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                ) : (
                  <Box sx={{ p: 3, textAlign: 'center', bgcolor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      {loadingSuggestions ? 'KI analysiert Ihr Unternehmen...' : 'Keine KI-Empfehlungen verfügbar'}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {activeTab === 1 && (
            <Box>
              {/* Ausgewählte Attribute */}
              <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckIcon color="success" />
                Ausgewählte Attribute ({selectedAttributes.length})
              </Typography>
              
              {selectedAttributes.length === 0 ? (
                <Alert severity="info">
                  Noch keine Attribute ausgewählt. Nutzen Sie die KI-Empfehlungen oder durchsuchen Sie alle verfügbaren Attribute.
                </Alert>
              ) : (
                <Grid container spacing={2}>
                  {selectedAttributes.map((attribute) => (
                    <Grid item xs={12} sm={6} md={4} key={attribute.key}>
                      <Card sx={{ p: 2, border: '1px solid #4caf50', bgcolor: '#f1f8e9' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                              {attribute.label}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>
                              {attribute.description}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() => removeAttribute(attribute.key)}
                            sx={{ color: '#f44336' }}
                          >
                            <RemoveIcon />
                          </IconButton>
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          )}

          {activeTab === 2 && (
            <Box>
              {/* Alle Attribute */}
              <Typography variant="h6" sx={{ mb: 3 }}>
                Alle verfügbaren Attribute
              </Typography>
              
              {/* Search */}
              <Autocomplete
                options={allAttributes}
                getOptionLabel={(option) => option.label}
                filterOptions={(options, { inputValue }) => {
                  return options.filter(option =>
                    option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
                    option.description.toLowerCase().includes(inputValue.toLowerCase())
                  ).slice(0, 50);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Attribut suchen..."
                    fullWidth
                    sx={{ mb: 3 }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />
                    }}
                  />
                )}
                renderOption={(props, option) => {
                  const isSelected = selectedAttributes.some(attr => attr.key === option.key);
                  
                  return (
                    <Box
                      component="li"
                      {...props}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 2,
                        border: isSelected ? '2px solid #4caf50' : '1px solid transparent',
                        bgcolor: isSelected ? '#f1f8e9' : 'white',
                        borderRadius: 1,
                        mb: 0.5,
                        '&:hover': { 
                          bgcolor: isSelected ? '#e8f5e8' : '#f5f5f5',
                          borderColor: isSelected ? '#4caf50' : '#2196f3'
                        }
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography 
                            variant="body2" 
                            fontWeight="600"
                            sx={{ color: isSelected ? '#2e7d32' : '#1a1a1a' }}
                          >
                            {option.label}
                          </Typography>
                          {isSelected && (
                            <Chip 
                              label="AUSGEWÄHLT" 
                              size="small" 
                              sx={{ 
                                bgcolor: '#4caf50',
                                color: 'white',
                                fontSize: '0.6rem',
                                height: 18
                              }} 
                            />
                          )}
                        </Box>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: isSelected ? '#4caf50' : 'text.secondary',
                            fontSize: '0.75rem'
                          }}
                        >
                          {option.description}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isSelected) {
                            removeAttribute(option.key);
                          } else {
                            addAttribute(option);
                          }
                        }}
                        sx={{
                          bgcolor: isSelected ? '#4caf50' : '#2196f3',
                          color: 'white',
                          '&:hover': {
                            bgcolor: isSelected ? '#45a049' : '#1976d2'
                          }
                        }}
                      >
                        {isSelected ? <CheckIcon sx={{ fontSize: '1rem' }} /> : <AddIcon sx={{ fontSize: '1rem' }} />}
                      </IconButton>
                    </Box>
                  );
                }}
                onChange={(event, value) => {
                  if (value) {
                    addAttribute(value);
                  }
                }}
                value={null}
              />

              {/* Categories */}
              <Box sx={{ mt: 3 }}>
                {renderAttributesByCategory()}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ 
        p: 4, 
        bgcolor: '#fafafa', 
        borderTop: '1px solid #e5e5e5',
        gap: 2,
        justifyContent: 'space-between'
      }}>
        <Button 
          onClick={onClose}
          sx={{
            color: '#666',
            fontWeight: 500,
            px: 3,
            py: 1,
            textTransform: 'none',
            '&:hover': { bgcolor: '#f5f5f5' }
          }}
        >
          Abbrechen
        </Button>
        <Button 
          variant="contained" 
          onClick={handleSave}
          disabled={selectedAttributes.length === 0}
          sx={{
            bgcolor: '#22c55e',
            color: 'white',
            fontWeight: 600,
            px: 4,
            py: 1,
            textTransform: 'none',
            '&:hover': { bgcolor: '#16a34a' }
          }}
        >
          Attribute speichern ({selectedAttributes.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AttributeManager;